"use client";

import * as React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import MeetingRoomSharpIcon from "@mui/icons-material/MeetingRoomSharp";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import axios from "axios";
import { Box, Typography, useTheme, Paper, Popper, Fade } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { setTokens } from "../redux/actions/tokenActions";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import MuiListItemIcon from "@mui/material/ListItemIcon";
import PersonIcon from "@mui/icons-material/Person";
import StorefrontIcon from "@mui/icons-material/Storefront";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import Swal from "sweetalert2";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import "../assets/css/animations.css";

import ReceiptIcon from "@mui/icons-material/Receipt";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { useTranslation } from "react-i18next";

const ListItemIcon = styled(MuiListItemIcon, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ open, active }) => ({
  color: active ? "#ffffff !important" : "#ffffff !important",
  backgroundColor: active ? "#f08049" : "transparent",
  width: !open ? 48 : 36,
  height: !open ? 48 : 36,
  maxWidth: !open ? 48 : 36,
  maxHeight: !open ? 48 : 36,
  minWidth: "unset",
  display: "flex",
  alignItems: "center",
  justifyContent: !open ? "center" : active ? "center" : "flex-start",
  borderRadius: ".5rem",
  marginRight: open ? "1rem" : "0",
  transition: "background 0.3s",
}));

const HoverMenu = styled(Paper)(({ theme }) => ({
  position: "absolute",
  left: "100%",
  top: 0,
  marginLeft: "4px", // Reduced gap
  minWidth: "200px",
  zIndex: 1300,
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  borderRadius: "8px",
  overflow: "hidden",
}));

const IconComp = ({ active, open, url }) => {
  return (
    <Box
      component="img"
      sx={{
        width: open ? 30 : 20,
        height: open ? 30 : 20,
      }}
      alt="partner_portal"
      src={url}
    />
  );
};

export function MainListItems({ open, setOpen }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const profile = useSelector((state) => state.profile.profile);

  const menuItems = [
    {
      name: t("dashboard"),
      url: "/",
      icon: <DashboardIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("quotes"),
      url: "/quotes",
      icon: <ReceiptLongIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("roomFinder"),
      url: "/room-finder",
      icon: <MeetingRoomSharpIcon sx={{ color: "#637381" }} />,
    },
    ...(profile?.role === "Admin"
      ? [
        {
          name: t("discountRequest"),
          url: "/discount-request",
          icon: <ReceiptLongIcon sx={{ color: "#637381" }} />,
        },
      ]
      : []),
    {
      name: t("myInvoices"),
      url: "/myinvoices",
      icon: <ReceiptIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("myRequests"),
      url: "/myrequests",
      icon: <DescriptionIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("additionalServices"),
      url: "/additional-services",
      icon: <RoomServiceIcon sx={{ color: "#637381" }} />,
      isNew: true,
    },
    {
      name: t("tickets"),
      url: "/tickets",
      icon: <ConfirmationNumberIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("shop"),
      url: "/shop",
      icon: <StorefrontIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("myBookings"),
      url: "/mybookings",
      icon: <BookOnlineIcon sx={{ color: "#637381" }} />,
    },
    {
      name: t("userGuide"),
      url: "/user-guide",
      icon: <MenuBookIcon sx={{ color: "#637381" }} />,
    },
  ];


  const dropdownMenus = [
    {
      id: "customerSupport",
      name: t("customerSupport"),
      icon: <HeadsetMicIcon sx={{ color: "#637381" }} />,
      stateKey: "ticketSystemDropdown",
      items: [
        {
          name: t("createTicket"),
          url: "/create-ticket",
        },
        {
          name: t("ticketList"),
          url: "/requests",
        },
      ],
      checkActive: (path) =>
        path.includes("create-ticket") || path.includes("/requests"),
    },
    {
      id: "settings",
      name: t("settings"),
      icon: <ManageAccountsIcon sx={{ color: "#637381" }} />,
      stateKey: "settingsDropdown",
      items: [
        {
          id: "add-user",
          name: t("addUser"),
          url: "/add-user",
        },
        {
          id: "add-branch",
          name: t("addBranch"),
          url: "/add-branch",
        },
      ],
      checkActive: (path) =>
        path.includes("add-user") || path.includes("add-branch"),
      showIf: () => profile?.account_type !== "user",
    },
  ];

  const branchesArray = useSelector((state) => state?.branches?.branches);
  const agencyArray = useSelector((state) => state?.branches?.agency);
  const selectedBranchesString = useSelector(
    (state) => state?.branches?.branchesString
  );
  const isMobile = window.innerWidth < 768;

  const isCurrentUrlInSettings = dropdownMenus
    .find((menu) => menu.id === "settings")
    ?.items.some((item) => window.location.href.includes(item.url));

  const isCurrentUrlInTicketSystem = dropdownMenus
    .find((menu) => menu.id === "customerSupport")
    ?.items.some((item) => window.location.href.includes(item.url));

  const [settingsDropdown, setSettingsDropdown] = React.useState(
    isCurrentUrlInSettings
  );
  const [ticketSystemDropdown, setTicketSystemDropdown] = React.useState(
    isCurrentUrlInTicketSystem
  );

  // Hover menu states
  const [hoverMenuAnchor, setHoverMenuAnchor] = React.useState(null);
  const [hoverMenuItems, setHoverMenuItems] = React.useState([]);
  const [hoverMenuTitle, setHoverMenuTitle] = React.useState("");
  const [isHoveringMenu, setIsHoveringMenu] = React.useState(false);
  const [isHoveringTrigger, setIsHoveringTrigger] = React.useState(false);

  // Timeout ref for delayed closing
  const hoverTimeoutRef = React.useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const lightMode = useSelector((state) => state.mode.lightMode);

  const logOutFunc = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const token = localStorage.getItem("loginUser");
    Swal.fire({
      icon: "info",
      title: "Logging Out...",
      showConfirmButton: false,
      allowOutsideClick: false,
    });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}auth/logout`,
        {
          refresh: token,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            XRefreshToken: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      dispatch(setTokens(null, null));
      localStorage.removeItem("accessToken");
      localStorage.removeItem("loginUser");
      Swal.close();
      navigate("/login");
    } catch (error) {
      console.log(error);
    }
  };

  const [dropdownStyles, setDropdownStyles] = React.useState({
    background: lightMode ? "#fff" : "#1e1e1e",
    color: lightMode ? "#637381" : "#fff",
  });

  const [settingsDropdownStyles, setSettingsDropdownStyles] = React.useState({
    background: lightMode ? "#fff" : "#1e1e1e",
    color: lightMode ? "#637381" : "#fff",
  });

  const [ticketSystemDropdownStyles, setTicketSystemDropdownStyles] =
    React.useState({
      background: lightMode ? "#fff" : "#1e1e1e",
      position: "relative",
      color: lightMode ? "#637381" : "#fff",
    });

  React.useEffect(() => {
    setDropdownStyles((prev) => ({
      ...prev,
      background: lightMode ? "#fff" : "#1e1e1e",
      color: lightMode ? "#637381" : "#fff",
    }));
    setSettingsDropdownStyles((prev) => ({
      ...prev,
      background: lightMode ? "#fff" : "#1e1e1e",
      color: lightMode ? "#637381" : "#fff",
    }));
    setTicketSystemDropdownStyles((prev) => ({
      ...prev,
      background: lightMode ? "#fff" : "#1e1e1e",
      color: lightMode ? "#637381" : "#fff",
    }));
  }, [lightMode]);

  React.useEffect(() => {
    if (settingsDropdown) {
      setSettingsDropdownStyles({
        display: "block",
        background: lightMode ? "#fff" : "#1e1e1e",
        color: "#637381",
        position: "relative",
        marginLeft: "28px",
        "&:before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "2px",
          height: "calc(100% - 24px)",
          background: "#edeff2",
        },
      });
    } else {
      setSettingsDropdownStyles({
        display: "none",
      });
    }
  }, [settingsDropdown]);

  React.useEffect(() => {
    if (ticketSystemDropdown) {
      setTicketSystemDropdownStyles({
        display: "block",
        background: lightMode ? "#fff" : "#1e1e1e",
        color: "#637381",
        position: "relative",
        marginLeft: "28px",
        "&:before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "2px",
          height: "calc(100% - 24px)",
          background: "#edeff2",
        },
      });
    } else {
      setTicketSystemDropdownStyles({
        display: "none",
      });
    }
  }, [ticketSystemDropdown]);

  // Clear timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Close menu when neither trigger nor menu is hovered
  React.useEffect(() => {
    if (!isHoveringTrigger && !isHoveringMenu && !open) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setHoverMenuAnchor(null);
        setHoverMenuItems([]);
        setHoverMenuTitle("");
      }, 150); // Small delay to allow moving between elements
    } else {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    }
  }, [isHoveringTrigger, isHoveringMenu, open]);

  const handleMouseEnter = (event, menu) => {
    if (!open && menu.items) {
      setIsHoveringTrigger(true);
      setHoverMenuAnchor(event.currentTarget);
      setHoverMenuItems(menu.items);
      setHoverMenuTitle(menu.name);

      // Clear any pending close timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHoveringTrigger(false);
  };

  const handleMenuMouseEnter = () => {
    setIsHoveringMenu(true);
  };

  const handleMenuMouseLeave = () => {
    setIsHoveringMenu(false);
  };

  const handleDropdownClick = (menu) => {
    if (open) {
      if (menu.id === "customerSupport") {
        setTicketSystemDropdown(!ticketSystemDropdown);
      } else if (menu.id === "settings") {
        setSettingsDropdown(!settingsDropdown);
      }
    }
  };

  return (
    <React.Fragment>
      {/* First 5 menu items */}
      {menuItems.slice(0, 5).map((item, index) => {
        const isActive =
          item.url === "/"
            ? window.location.pathname === "/"
            : window.location.pathname.startsWith(item.url);

        return (
          <ListItemButton
            key={`menu-item-${index}`}
            sx={{
              padding: open ? ".5rem 1rem" : "0",
              borderRadius: ".5rem",
              color: lightMode ? (isActive ? "#ffffff" : "#637381") : "#fff",
              justifyContent: "center",
              "&:hover": {
                background: isActive
                  ? lightMode
                    ? theme.palette.primary.light
                    : "#3b3a3a"
                  : lightMode
                    ? "#f6f7f8"
                    : "#292929",
              },
              background: isActive
                ? lightMode
                  ? theme.palette.primary.light
                  : theme.palette.primary.light
                : "unset",
            }}
            href={item.url}
          >
            <ListItemIcon open={open} active={isActive}>
              {React.cloneElement(item.icon, {
                sx: { color: isActive ? "#fff" : "#637381" },
              })}
            </ListItemIcon>
            {open ? (
              <ListItemText
                sx={{
                  span: {
                    fontSize: "14px !important",
                    color: isActive ? "#ffffff" : "#637381",
                  },
                }}
                primary={item.name}
              />
            ) : null}
            {open && item.isNew ? (
              <ListItemText
                sx={{
                  maxWidth: "fit-content",
                  marginLeft: ".5rem",
                  animation: " blinker 1s linear infinite",
                  backgroundColor: "#ffffff",
                  padding: ".25rem .5rem",
                  borderRadius: "2rem",
                }}
              >
                <Typography
                  sx={{
                    color: "#444444",
                  }}
                  component="span"
                  variant="body2"
                  fontWeight={500}
                >
                  {t("new")}
                </Typography>
              </ListItemText>
            ) : null}
          </ListItemButton>
        );
      })}

      {/* Customer Support dropdown */}
      {dropdownMenus
        .filter((menu) => menu.id === "customerSupport")
        .map((menu, index) => {
          if (menu.showIf && !menu.showIf()) return null;

          const isDropdownOpen = ticketSystemDropdown;
          const dropdownItems = menu.items;
          const isActive = menu.checkActive(window.location.pathname);

          return (
            <React.Fragment key="dropdown-customer-support">
              <Box
                sx={{ position: "relative" }}
                onMouseEnter={(e) => handleMouseEnter(e, menu)}
                onMouseLeave={handleMouseLeave}
              >
                <ListItemButton
                  sx={{
                    padding: open ? ".5rem 1rem" : "0",
                    borderRadius: ".5rem",
                    color: lightMode
                      ? isActive
                        ? "#ffffff"
                        : "#637381"
                      : "#fff",
                    justifyContent: "center",
                    borderBottom: isDropdownOpen && open && "1px solid #ffffff",
                    background: isActive
                      ? theme.palette.primary.light
                      : isDropdownOpen
                        ? lightMode
                          ? "#f6f7f8"
                          : "#393939"
                        : lightMode
                          ? "#fff"
                          : "#1e1e1e",
                    "&:hover": {
                      background: isActive
                        ? theme.palette.primary.light
                        : lightMode
                          ? "#f6f7f8"
                          : "#292929",
                    },
                  }}
                  onClick={() => handleDropdownClick(menu)}
                >
                  <ListItemIcon open={open} active={isActive}>
                    {React.cloneElement(menu.icon, {
                      sx: { color: isActive ? "#ffffff" : "#637381" },
                    })}
                  </ListItemIcon>
                  {open ? (
                    <ListItemText
                      sx={{
                        span: {
                          fontSize: "14px !important",
                          color: isActive ? "#ffffff" : "#637381",
                        },
                      }}
                      primary={menu.name}
                    />
                  ) : null}
                  {open && (
                    <ListItemIcon
                      sx={{
                        display: "block",
                        justifyContent: "flex-end",
                      }}
                    >
                      {isDropdownOpen ? (
                        <ArrowDropUpIcon
                          sx={{ color: isActive ? "#ffffff" : "#637381" }}
                        />
                      ) : (
                        <ArrowDropDownIcon
                          sx={{ color: isActive ? "#ffffff" : "#637381" }}
                        />
                      )}
                    </ListItemIcon>
                  )}
                </ListItemButton>

                {open && isDropdownOpen && (
                  <Box sx={ticketSystemDropdownStyles}>
                    {dropdownItems.map((item, idx) => {
                      const isUrlMatched = window.location.href.includes(
                        item.url
                      );

                      return (
                        <ListItemButton
                          href={`${item.url}`}
                          key={`${item.name}-${idx}`}
                          sx={{
                            "&:before": {
                              left: "0px",
                              content: "''",
                              position: "absolute",
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#EDEFF2",
                              mask: "url(data:image/svg+xml;charset=utf-8;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxNCcgaGVpZ2h0PScxNCcgZmlsbD0nbm9uZScgdmlld0JveD0nMCAwIDE0IDE0Jz48cGF0aCBkPSdNMSAxdjRhOCA4IDAgMCAwIDggOGg0JyBzdHJva2U9JyNlZmVmZWYnIHN0cm9rZS13aWR0aD0nMicgc3Ryb2tlLWxpbmVjYXA9J3JvdW5kJy8+PC9zdmc+) 50% 50% / 100% no-repeat",
                              transform:
                                "translate(calc(12px * -1), calc(12px * -0.4)",
                            },
                            "&:hover": {
                              background: isUrlMatched
                                ? lightMode
                                  ? "#f6f7f8"
                                  : "#3b3a3a"
                                : lightMode
                                  ? "#f6f7f8"
                                  : "#292929",
                            },
                            background: isUrlMatched
                              ? lightMode
                                ? "#f6f7f8"
                                : theme.palette.primary.light
                              : "unset",
                            cursor: "pointer",
                            pointerEvents: "auto",
                            color: isUrlMatched ? "green" : "#637381",
                          }}
                        >
                          <ListItemText
                            sx={{
                              span: {
                                paddingLeft: "12px !important",
                                fontSize: "14px !important",
                                color: isUrlMatched
                                  ? lightMode
                                    ? "#1c252e"
                                    : "#fff"
                                  : "#637381",
                              },
                            }}
                            primary={`${item.name}`}
                          />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </React.Fragment>
          );
        })}

      {/* Middle menu items */}
      {menuItems.slice(5, 9).map((item, index) => {
        const isActive = window.location.pathname.includes(
          item.url.substring(1)
        );
        return (
          <ListItemButton
            key={`menu-item-middle-${index}`}
            sx={{
              padding: open ? ".5rem 1rem" : "0",
              borderRadius: ".5rem",
              color: lightMode ? (isActive ? "#ffffff" : "#637381") : "#fff",
              justifyContent: "center",
              "&:hover": {
                background: window.location.pathname.includes(item.url)
                  ? lightMode
                    ? theme.palette.primary.light
                    : "#3b3a3a"
                  : lightMode
                    ? "#f6f7f8"
                    : "#292929",
              },
              background: window.location.href.includes(item.url)
                ? lightMode
                  ? theme.palette.primary.light
                  : theme.palette.primary.light
                : "unset",
            }}
            href={item.url}
          >
            <ListItemIcon open={open} active={isActive}>
              {React.cloneElement(item.icon, {
                sx: { color: isActive ? "#ffffff" : "#637381" },
              })}
            </ListItemIcon>
            {open ? (
              <ListItemText
                sx={{
                  span: {
                    fontSize: "14px !important",
                    color: isActive ? "#ffffff" : "#637381",
                  },
                }}
                primary={item.name}
              />
            ) : null}
            {open && item.isNew ? (
              <ListItemText
                sx={{
                  maxWidth: "fit-content",
                  marginLeft: ".5rem",
                  animation: " blinker 1s linear infinite",
                  backgroundColor: "#f08049",
                  padding: ".25rem .5rem",
                  borderRadius: "2rem",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                  }}
                  component="span"
                  variant="body2"
                  fontWeight={500}
                >
                  {t("new")}
                </Typography>
              </ListItemText>
            ) : null}
          </ListItemButton>
        );
      })}

      {/* Settings dropdown */}
      {dropdownMenus
        .filter((menu) => menu.id === "settings")
        .map((menu, index) => {
          if (menu.showIf && !menu.showIf()) return null;

          const isDropdownOpen = settingsDropdown;
          const dropdownItems = menu.items;
          const isActive = menu.checkActive(window.location.pathname);

          return (
            <React.Fragment key="dropdown-settings">
              <Box
                sx={{ position: "relative" }}
                onMouseEnter={(e) => handleMouseEnter(e, menu)}
                onMouseLeave={handleMouseLeave}
              >
                <ListItemButton
                  sx={{
                    padding: open ? ".5rem 1rem" : "0",
                    borderRadius: ".5rem",
                    color: lightMode
                      ? isActive
                        ? "#ffffff"
                        : "#637381"
                      : "#fff",
                    justifyContent: "center",
                    borderBottom: isDropdownOpen && open && "1px solid #ffffff",
                    background: isActive
                      ? theme.palette.primary.light
                      : isDropdownOpen
                        ? lightMode
                          ? "#f6f7f8"
                          : "#393939"
                        : lightMode
                          ? "#fff"
                          : "#1e1e1e",
                    "&:hover": {
                      background: isActive
                        ? theme.palette.primary.light
                        : lightMode
                          ? "#f6f7f8"
                          : "#292929",
                    },
                  }}
                  onClick={() => handleDropdownClick(menu)}
                >
                  <ListItemIcon open={open} active={isActive}>
                    {React.cloneElement(menu.icon, {
                      sx: { color: isActive ? "#ffffff" : "#637381" },
                    })}
                  </ListItemIcon>
                  {open ? (
                    <ListItemText
                      sx={{
                        span: {
                          fontSize: "14px !important",
                          color: isActive ? "#ffffff" : "#637381",
                        },
                      }}
                      primary={menu.name}
                    />
                  ) : null}
                  {open && (
                    <ListItemIcon
                      sx={{
                        display: "block",
                        justifyContent: "flex-end",
                      }}
                    >
                      {isDropdownOpen ? (
                        <ArrowDropUpIcon
                          sx={{ color: isActive ? "#ffffff" : "#637381" }}
                        />
                      ) : (
                        <ArrowDropDownIcon
                          sx={{ color: isActive ? "#ffffff" : "#637381" }}
                        />
                      )}
                    </ListItemIcon>
                  )}
                </ListItemButton>

                {open && isDropdownOpen && (
                  <Box sx={settingsDropdownStyles}>
                    {dropdownItems.map((item, idx) => {
                      const isUrlMatched = window.location.href.includes(
                        item.url
                      );

                      if (
                        profile?.account_type === "sub_branch" &&
                        item?.id === "add-branch"
                      ) {
                        return null;
                      }

                      return (
                        <ListItemButton
                          href={`${item.url}`}
                          key={`${item.name}-${idx}`}
                          sx={{
                            "&:before": {
                              left: "0px",
                              content: "''",
                              position: "absolute",
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#EDEFF2",
                              mask: "url(data:image/svg+xml;charset=utf-8;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxNCcgaGVpZ2h0PScxNCcgZmlsbD0nbm9uZScgdmlld0JveD0nMCAwIDE0IDE0Jz48cGF0aCBkPSdNMSAxdjRhOCA4IDAgMCAwIDggOGg0JyBzdHJva2U9JyNlZmVmZWYnIHN0cm9rZS13aWR0aD0nMicgc3Ryb2tlLWxpbmVjYXA9J3JvdW5kJy8+PC9zdmc+) 50% 50% / 100% no-repeat",
                              transform:
                                "translate(calc(12px * -1), calc(12px * -0.4)",
                            },
                            "&:hover": {
                              background: isUrlMatched
                                ? lightMode
                                  ? "#f6f7f8"
                                  : "#3b3a3a"
                                : lightMode
                                  ? "#f6f7f8"
                                  : "#292929",
                            },
                            background: isUrlMatched
                              ? lightMode
                                ? "#f6f7f8"
                                : theme.palette.primary.light
                              : "unset",
                            cursor: "pointer",
                            pointerEvents: "auto",
                            color: isUrlMatched ? "green" : "#637381",
                          }}
                        >
                          <ListItemText
                            sx={{
                              span: {
                                paddingLeft: "12px !important",
                                fontSize: "14px !important",
                                color: isUrlMatched
                                  ? lightMode
                                    ? "#1c252e"
                                    : "#fff"
                                  : "#637381",
                              },
                            }}
                            primary={`${item.name}`}
                          />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </React.Fragment>
          );
        })}

      {/* Last menu item */}
      {menuItems.slice(9).map((item, index) => {
        const isActive = window.location.pathname.includes(
          item.url.substring(1)
        );
        return (
          <ListItemButton
            key={`menu-item-last-${index}`}
            sx={{
              padding: open ? ".5rem 1rem" : "0",
              borderRadius: ".5rem",
              color: lightMode ? (isActive ? "#ffffff" : "#637381") : "#fff",
              justifyContent: "center",
              "&:hover": {
                background: window.location.pathname.includes(item.url)
                  ? lightMode
                    ? theme.palette.primary.light
                    : "#3b3a3a"
                  : lightMode
                    ? "#f6f7f8"
                    : "#292929",
              },
              background: window.location.href.includes(item.url)
                ? lightMode
                  ? theme.palette.primary.light
                  : theme.palette.primary.light
                : "unset",
            }}
            href={item.url}
          >
            <ListItemIcon open={open} active={isActive}>
              {React.cloneElement(item.icon, {
                sx: { color: isActive ? "#ffffff" : "#637381" },
              })}
            </ListItemIcon>
            {open ? (
              <ListItemText
                sx={{
                  span: {
                    fontSize: "14px !important",
                    color: isActive ? "#ffffff" : "#637381",
                  },
                }}
                primary={item.name}
              />
            ) : null}
            {open && item.isNew ? (
              <ListItemText
                sx={{
                  maxWidth: "fit-content",
                  marginLeft: ".5rem",
                  animation: " blinker 1s linear infinite",
                  backgroundColor: "#ffffff",
                  padding: ".25rem .5rem",
                  borderRadius: "2rem",
                }}
              >
                <Typography
                  sx={{
                    color: "#444444",
                  }}
                  component="span"
                  variant="body2"
                  fontWeight={500}
                >
                  New
                </Typography>
              </ListItemText>
            ) : null}
          </ListItemButton>
        );
      })}

      {/* Mobile-specific items */}
      {isMobile ? (
        <>
          <ListItemButton
            href={`${!token.accessToken ? "/login" : ""}`}
            onClick={() => token.accessToken && logOutFunc()}
          >
            <ListItemIcon>
              {token.accessToken ? (
                <LogoutIcon sx={{ color: "#637381" }} />
              ) : (
                <LoginIcon sx={{ color: "#637381" }} />
              )}
            </ListItemIcon>
            <ListItemText
              sx={{
                color: '#637381'
              }}
              primary={`${token.accessToken ? t("logOut") : t("logIn")}`}
            />
          </ListItemButton>

          <ListItemButton
            sx={{
              "&:hover": {
                background: window.location.pathname.includes("/profile")
                  ? lightMode
                    ? theme.palette.primary.light
                    : "#3b3a3a"
                  : lightMode
                    ? "#f6f7f8"
                    : "#292929",
              },
              background: window.location.href.includes("/profile")
                ? lightMode
                  ? theme.palette.primary.light
                  : theme.palette.primary.light
                : "unset",
              cursor: "pointer !important",
              pointerEvents: "auto !important",
            }}
            href={`${!token.accessToken ? "/login" : "/profile"}`}
          >
            <ListItemIcon>
              <PersonIcon sx={{ color: "#637381" }} />
            </ListItemIcon>
            {open ? (
              <ListItemText
                sx={{
                  span: {
                    fontSize: "14px !important",
                    color: '#637381'
                  },
                }}
                primary={t("profile")}
              />
            ) : null}
          </ListItemButton>
        </>
      ) : null}

      {/* Hover Menu for Closed Sidebar */}
      <Popper
        open={Boolean(hoverMenuAnchor) && !open}
        anchorEl={hoverMenuAnchor}
        placement="right-start"
        transition
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4], // Reduced offset
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <HoverMenu
              sx={{
                backgroundColor: lightMode ? "#fff" : "#1e1e1e",
                color: lightMode ? "#1c252e" : "#fff",
              }}
              onMouseEnter={handleMenuMouseEnter}
              onMouseLeave={handleMenuMouseLeave}
            >
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    borderBottom: `1px solid ${lightMode ? "#f0f0f0" : theme.palette.primary.light
                      }`,
                    mb: 1,
                  }}
                >
                  {hoverMenuTitle}
                </Typography>
                {hoverMenuItems.map((item, idx) => {
                  const isUrlMatched = window.location.href.includes(item.url);

                  // Special handling for settings items
                  if (
                    hoverMenuTitle === "Settings" &&
                    profile?.account_type === "sub_branch" &&
                    item?.id === "add-branch"
                  ) {
                    return null;
                  }

                  return (
                    <ListItemButton
                      key={`hover-${item.name}-${idx}`}
                      href={item.url}
                      sx={{
                        py: 1,
                        px: 2,
                        borderRadius: "4px",
                        margin: "2px 0",
                        "&:hover": {
                          background: lightMode
                            ? "#f6f7f8"
                            : theme.palette.primary.light,
                        },
                        background: isUrlMatched
                          ? lightMode
                            ? "#f0f0f0"
                            : theme.palette.primary.light
                          : "transparent",
                      }}
                    >
                      <ListItemText
                        sx={{
                          span: {
                            fontSize: "14px !important",
                            color: isUrlMatched
                              ? lightMode
                                ? "#1c252e"
                                : "#fff"
                              : "#637381",
                            fontWeight: isUrlMatched ? 500 : 400,
                          },
                        }}
                        primary={item.name}
                      />
                    </ListItemButton>
                  );
                })}
              </Box>
            </HoverMenu>
          </Fade>
        )}
      </Popper>
    </React.Fragment>
  );
}
